export const CURRENCY_CODE_MAX_LENGTH = 3;

export interface IAddCurrencyFormValidation {
  codeErrorText: string;
  nameErrorText: string;
  hasFilledCode: boolean;
  hasFilledName: boolean;
  hasFilledExchangeRate: boolean;
  isSubmitEnabled: boolean;
}

interface IAddCurrencyFormValidationParams {
  currencyCode: string;
  currencyName: string;
  exchangeRate: number | null;
  codeExists: boolean;
  nameExists: boolean;
  isCheckingAvailability: boolean;
  isSaving: boolean;
}

/** Indica si el código de moneda cumple el límite de caracteres permitido. */
export const isValidCurrencyCodeLength = (code: string): boolean => {
  const normalizedCode = code.trim();
  return normalizedCode.length > 0 && normalizedCode.length <= CURRENCY_CODE_MAX_LENGTH;
};

/** Indica si debe consultarse la disponibilidad del código de moneda. */
export const shouldCheckCurrencyCodeAvailability = (code: string): boolean => {
  const normalizedCode = code.trim();
  return !!normalizedCode && isValidCurrencyCodeLength(normalizedCode);
};

/** Indica si debe consultarse la disponibilidad del nombre de moneda. */
export const shouldCheckCurrencyNameAvailability = (name: string): boolean => {
  return !!name.trim();
};

/** Calcula errores y estado de envío del formulario de creación de moneda. */
export const getAddCurrencyFormValidation = ({
  currencyCode,
  currencyName,
  exchangeRate,
  codeExists,
  nameExists,
  isCheckingAvailability,
  isSaving
}: IAddCurrencyFormValidationParams): IAddCurrencyFormValidation => {
  const isCodeLengthValid = isValidCurrencyCodeLength(currencyCode);
  const codeLengthErrorText =
    currencyCode.trim() && !isCodeLengthValid
      ? `El código debe tener máximo ${CURRENCY_CODE_MAX_LENGTH} caracteres`
      : '';
  const codeExistsErrorText = codeExists ? 'El código ya existe' : '';
  const codeErrorText = codeLengthErrorText || codeExistsErrorText;
  const nameErrorText = nameExists ? 'La moneda ya existe' : '';
  const hasFilledCode = !!currencyCode.trim();
  const hasFilledName = !!currencyName.trim();
  const hasFilledExchangeRate = exchangeRate !== null && exchangeRate > 0;
  const isAvailabilityValid = !codeExists && !nameExists;
  const isSubmitEnabled =
    hasFilledCode &&
    isCodeLengthValid &&
    hasFilledName &&
    hasFilledExchangeRate &&
    isAvailabilityValid &&
    !isCheckingAvailability &&
    !isSaving;

  return {
    codeErrorText,
    nameErrorText,
    hasFilledCode,
    hasFilledName,
    hasFilledExchangeRate,
    isSubmitEnabled
  };
};

/** Convierte la tasa de cambio del catálogo de monedas a número o null. */
export const parseCurrencyTableExchangeRate = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};
